import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rail", "pill", "grid"]

  connect() {
    this.mq = window.matchMedia("(max-width: 1535px)")
    this.boundUpdate = this.update.bind(this)
    this.mq.addEventListener("change", this.boundUpdate)

    this.drawerOpen = this.isDrawerCurrentlyOpen()
    this.boundDrawerOpen = this.onDrawerOpen.bind(this)
    this.boundDrawerClose = this.onDrawerClose.bind(this)
    this.boundDrawerCloseAll = this.onDrawerCloseAll.bind(this)
    document.addEventListener("drawer.open", this.boundDrawerOpen)
    document.addEventListener("drawer.close", this.boundDrawerClose)
    document.addEventListener("drawer.closeAll", this.boundDrawerCloseAll)

    this.applyImmediate()
  }

  disconnect() {
    this.mq.removeEventListener("change", this.boundUpdate)
    document.removeEventListener("drawer.open", this.boundDrawerOpen)
    document.removeEventListener("drawer.close", this.boundDrawerClose)
    document.removeEventListener("drawer.closeAll", this.boundDrawerCloseAll)
  }

  onDrawerOpen() {
    this.drawerOpen = true
    if (!this.shouldCollapse()) return
    if (this.hasPillTarget && this.pillTarget.style.display !== "none") return
    this.transitionToPill()
  }

  onDrawerClose() {
    // After closing one drawer, check if any are still open (stacked drawers)
    setTimeout(() => {
      this.drawerOpen = this.isDrawerCurrentlyOpen()
      if (this.shouldCollapse()) return
      if (this.hasRailTarget && this.railTarget.style.display !== "none") return
      this.transitionToRail()
    }, 250)
  }

  onDrawerCloseAll() {
    this.drawerOpen = false
    if (this.shouldCollapse()) return
    this.transitionToRail()
  }

  isDrawerCurrentlyOpen() {
    const stack = document.querySelector('[data-drawer-target="drawerStack"]')
    if (!stack) return false
    const panels = stack.querySelectorAll('[data-drawer-target="panel"]')
    return Array.from(panels).some(p => !p.classList.contains("opacity-0") && !p.classList.contains("w-0"))
  }

  shouldCollapse() {
    return this.mq.matches || this.drawerOpen
  }

  applyImmediate() {
    const collapse = this.shouldCollapse()
    if (this.hasRailTarget) this.railTarget.style.display = collapse ? "none" : ""
    if (this.hasPillTarget) this.pillTarget.style.display = collapse ? "" : "none"
    this.updateGrid(collapse)
  }

  update() {
    if (this.shouldCollapse()) {
      this.transitionToPill()
    } else {
      this.transitionToRail()
    }
  }

  railItemContainers(rail) {
    return rail.querySelectorAll("[data-drawer-url]")
  }

  transitionToPill() {
    const rail = this.hasRailTarget ? this.railTarget : null
    const pill = this.hasPillTarget ? this.pillTarget : null

    if (rail) {
      const items = this.railItemContainers(rail)
      items.forEach((el, i) => {
        el.style.transition = `opacity 180ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 20}ms, transform 180ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 20}ms`
        el.style.opacity = "0"
        el.style.transform = "translateY(-4px)"
      })

      setTimeout(() => {
        rail.style.display = "none"
        this.updateGrid(true)
        items.forEach(el => {
          el.style.transition = ""; el.style.opacity = ""; el.style.transform = ""
        })

        if (pill) {
          pill.style.display = ""
          pill.style.opacity = "0"
          pill.style.transform = "translateX(-8px)"
          requestAnimationFrame(() => {
            pill.style.transition = "opacity 180ms cubic-bezier(0.22, 1, 0.36, 1), transform 180ms cubic-bezier(0.22, 1, 0.36, 1)"
            pill.style.opacity = "1"
            pill.style.transform = "translateX(0)"
            setTimeout(() => { pill.style.transition = ""; pill.style.opacity = ""; pill.style.transform = "" }, 200)
          })
        }
      }, 100)
    } else if (pill) {
      pill.style.display = ""
      this.updateGrid(true)
    }
  }

  transitionToRail() {
    const rail = this.hasRailTarget ? this.railTarget : null
    const pill = this.hasPillTarget ? this.pillTarget : null

    if (pill) {
      pill.style.transition = "opacity 180ms cubic-bezier(0.22, 1, 0.36, 1), transform 180ms cubic-bezier(0.22, 1, 0.36, 1)"
      pill.style.opacity = "0"
      pill.style.transform = "translateX(-8px)"

      setTimeout(() => {
        pill.style.display = "none"
        pill.style.transition = ""; pill.style.opacity = ""; pill.style.transform = ""

        if (rail) {
          this.updateGrid(false)
          rail.style.display = ""
          const items = this.railItemContainers(rail)
          items.forEach((el, i) => {
            el.style.opacity = "0"
            el.style.transform = "translateY(4px)"
            requestAnimationFrame(() => {
              el.style.transition = `opacity 180ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 20}ms, transform 180ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 20}ms`
              el.style.opacity = "1"
              el.style.transform = "translateY(0)"
            })
          })
          setTimeout(() => {
            items.forEach(el => {
              el.style.transition = ""; el.style.opacity = ""; el.style.transform = ""
            })
          }, 420)
        }
      }, 200)
    } else if (rail) {
      this.updateGrid(false)
      rail.style.display = ""
    }
  }

  updateGrid(collapsed) {
    if (!this.hasGridTarget) return
    const grid = this.gridTarget
    if (collapsed) {
      grid.classList.remove("2xl:grid-cols-[1fr_360px]")
      grid.classList.add("grid-cols-1")
    } else {
      grid.classList.remove("grid-cols-1")
      grid.classList.add("2xl:grid-cols-[1fr_360px]")
    }
  }
}
